import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { telefonoParaWhatsApp } from '../lib/marketing-body'
import { currentLocaleTag } from '../lib/formatting'
import {
  AlertTriangle,
  Building2,
  Calendar,
  ChevronLeft,
  ClipboardList,
  Coins,
  FileText,
  Globe,
  Loader2,
  Mail,
  MessageSquare,
  MessageCircle,
  Megaphone,
  Pencil,
  Phone,
  Plus,
  Receipt,
  Send,
  Target,
  TrendingUp,
  User as UserIcon,
  Users,
  Wallet,
} from 'lucide-react'
import ContactsPanel from '../components/clients/ContactsPanel'
import ClientDates from '../components/clients/ClientDates'
import NewCaseModal from '../components/cases/NewCaseModal'
import RenewalAlert from '../components/billing/RenewalAlert'
import { api, ApiError } from '../lib/api'
import { formatCents } from '../lib/money'
import { useAuth } from '../contexts/AuthContext'
import type {
  ClientDetail,
  MarketingTemplateDto,
  MarketingTemplateListResponse,
  EmailListItem,
  EmailListResponse,
  InvoiceListItem,
  InvoiceListResponse,
  MilestoneDto,
  MilestoneListResponse,
  OpportunityEventDto,
  OpportunityEventListResponse,
  OpportunityListItem,
  OpportunityListResponse,
  PipelineStage,
} from '@jr2os/shared'
import { useServiceLines } from '../lib/service-lines'
import ClientTags from '../components/clients/ClientTags'

type TabId =
  | 'resumen'
  | 'contactos'
  | 'oportunidades'
  | 'proyectos'
  | 'facturacion'
  | 'comunicaciones'

const TABS: { id: TabId; labelKey: string; icon: typeof FileText }[] = [
  { id: 'resumen', labelKey: 'tabs.resumen', icon: TrendingUp },
  { id: 'contactos', labelKey: 'tabs.contactos', icon: Users },
  { id: 'oportunidades', labelKey: 'tabs.oportunidades', icon: Target },
  { id: 'proyectos', labelKey: 'tabs.proyectos', icon: ClipboardList },
  { id: 'facturacion', labelKey: 'tabs.facturacion', icon: Receipt },
  { id: 'comunicaciones', labelKey: 'tabs.comunicaciones', icon: MessageSquare },
]

/**
 * Email enviado (placeholder; el agente #6 creará el endpoint /emails/sent).
 * Si el endpoint todavía no existe, toleramos el 404 y dejamos la lista vacía.
 */
interface SentEmailItem {
  id: string
  subject: string | null
  toAddresses: string[]
  sentAt: string
  status?: string | null
  campaignName?: string | null
}
interface SentEmailListResponse {
  items: SentEmailItem[]
  total?: number
}

export default function ClientDetailPage() {
  const { t } = useTranslation(['clients', 'opportunities'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const auth = useAuth()
  const canWrite = auth.status === 'authenticated' && auth.user.role !== 'LECTURA'

  const [client, setClient] = useState<ClientDetail | null>(null)
  const [opportunities, setOpportunities] = useState<OpportunityListItem[]>([])
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([])
  const [milestones, setMilestones] = useState<MilestoneDto[]>([])
  const [archivedEmails, setArchivedEmails] = useState<EmailListItem[]>([])
  const [sentEmails, setSentEmails] = useState<SentEmailItem[]>([])
  const [opportunityEvents, setOpportunityEvents] = useState<OpportunityEventDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabId>('resumen')
  const [showEditModal, setShowEditModal] = useState(false)
  const [showNewCase, setShowNewCase] = useState(false)

  const reload = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      // Carga principal del cliente (si falla, abortamos el resto)
      const { client: c } = await api.get<{ client: ClientDetail }>(`/clients/${id}`)
      setClient(c)

      // Cargas paralelas. Cada una tolera errores: si una rompe, dejamos esa
      // sección vacía pero el resto del dashboard sigue funcionando.
      const [oppsRes, invRes, milestRes, archEmailsRes, sentRes] = await Promise.allSettled([
        api.get<OpportunityListResponse>(`/opportunities?clientId=${id}&pageSize=100`),
        api.get<InvoiceListResponse>(`/invoices?clientId=${id}&pageSize=100`),
        api.get<MilestoneListResponse>(`/milestones?clientId=${id}`),
        api.get<EmailListResponse>(`/emails?archivedToClientId=${id}&pageSize=100`),
        // Endpoint creado por el agente #6 (envío de emails). Aún puede no existir.
        // ☠️ Aquí SÍ se piden las campañas. Esta pestaña es el historial COMPLETO
        // de lo que se ha hablado con el cliente, no el panel de «qué le he
        // escrito yo» de la ficha de edición: esconderlas aquí dejaría la
        // pestaña vacía para alguien que solo ha recibido boletines. Van
        // etiquetadas para no confundirlas con la correspondencia de verdad.
        api.get<SentEmailListResponse>(
          `/emails/sent?clientId=${id}&pageSize=100&includeCampaigns=true`,
        ),
      ])

      if (oppsRes.status === 'fulfilled') setOpportunities(oppsRes.value.items)
      else setOpportunities([])

      if (invRes.status === 'fulfilled') setInvoices(invRes.value.items)
      else setInvoices([])

      if (milestRes.status === 'fulfilled') setMilestones(milestRes.value.items)
      else setMilestones([])

      if (archEmailsRes.status === 'fulfilled') setArchivedEmails(archEmailsRes.value.items)
      else setArchivedEmails([])

      if (sentRes.status === 'fulfilled') setSentEmails(sentRes.value.items)
      else setSentEmails([])

      // Cargar eventos NOTE/CONTACT_LOG de todas las oportunidades del cliente
      // para mostrarlos en la pestaña Comunicaciones. Tolera errores por evento.
      if (oppsRes.status === 'fulfilled' && oppsRes.value.items.length > 0) {
        const evRes = await Promise.allSettled(
          oppsRes.value.items.slice(0, 10).map((op) =>
            api.get<OpportunityEventListResponse>(`/opportunities/${op.id}/events`)
          )
        )
        const allEvents: OpportunityEventDto[] = []
        for (const r of evRes) {
          if (r.status === 'fulfilled') {
            allEvents.push(...r.value.items.filter((e) => e.type === 'NOTE' || e.type === 'CONTACT_LOG'))
          }
        }
        allEvents.sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1))
        setOpportunityEvents(allEvents)
      } else {
        setOpportunityEvents([])
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError(t('errors.notFound'))
      } else {
        setError(err instanceof Error ? err.message : t('errors.loadFailed'))
      }
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void reload()
  }, [reload])

  if (loading && !client) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={18} className="animate-spin" /> {t('loading')}
      </div>
    )
  }

  if (error || !client) {
    return (
      <div className="max-w-xl mx-auto pt-12 surface p-8 text-center">
        <AlertTriangle size={32} className="mx-auto text-amber-700 dark:text-amber-300 mb-3" />
        <h2 className="text-xl font-serif font-semibold t-primary mb-1">{t('errors.unavailableTitle')}</h2>
        <p className="t-muted text-sm mb-4">{error ?? t('errors.clientNotFound')}</p>
        <Link
          to="/clients"
          className="inline-flex items-center gap-1.5 text-sm text-jr2-700 dark:text-jr2-300 hover:underline"
        >
          <ChevronLeft size={14} /> {t('backToList')}
        </Link>
      </div>
    )
  }

  const isEmpresa = client.type === 'EMPRESA'
  const initial = (client.displayName || '?').charAt(0).toUpperCase()

  return (
    <div className="space-y-6 animate-slideUp">
      {/* Cabecera */}
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link
            to="/clients"
            className="text-xs t-faint hover:underline inline-flex items-center gap-1"
          >
            <ChevronLeft size={12} /> {t('breadcrumb')}
          </Link>
          <div className="flex items-center gap-3 mt-2">
            <span
              className={[
                'w-12 h-12 rounded-xl grid place-items-center text-lg font-serif font-semibold',
                isEmpresa
                  ? 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-200 border border-indigo-500/30'
                  : 'bg-purple-500/15 text-purple-700 dark:text-purple-200 border border-purple-500/40',
              ].join(' ')}
              title={isEmpresa ? t('type.empresa') : t('type.personaFisica')}
            >
              {initial}
            </span>
            <div className="min-w-0">
              <h2 className="text-2xl font-serif font-semibold t-primary flex items-center gap-2">
                {client.displayName}
              </h2>
              {/* Las etiquetas van en la cabecera y no en una pestaña: son de lo
                  primero que hay que saber de alguien, y son la base de la
                  segmentación de campañas. */}
              <div className="mt-2">
                <ClientTags
                  clientId={client.id}
                  tags={client.tags}
                  canWrite={canWrite}
                  onChange={(tags) => setClient((prev) => (prev ? { ...prev, tags } : prev))}
                />
              </div>
              <div className="t-muted text-sm mt-1 flex items-center gap-2 flex-wrap">
                <span className="flex items-center gap-1">
                  {isEmpresa ? <Building2 size={14} /> : <UserIcon size={14} />}
                  {isEmpresa ? t('type.empresa') : t('type.personaFisica')}
                </span>
                <span className="t-faint">·</span>
                <span>{client.country || 'ES'}</span>
                {client.email && (
                  <>
                    <span className="t-faint">·</span>
                    <span className="flex items-center gap-1">
                      <Mail size={12} /> {client.email}
                    </span>
                  </>
                )}
                {client.phone && (
                  <>
                    <span className="t-faint">·</span>
                    <span className="flex items-center gap-1">
                      <Phone size={12} /> {client.phone}
                    </span>
                  </>
                )}
                {client.web && (
                  <>
                    <span className="t-faint">·</span>
                    <a
                      href={client.web.startsWith('http') ? client.web : `https://${client.web}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 hover:underline hover:text-jr2-700 dark:hover:text-jr2-200"
                    >
                      <Globe size={12} /> {client.web}
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/clients')}
            className="px-3 py-2 rounded-lg text-sm border border-base t-secondary hover:bg-hover hover:text-jr2-700 dark:hover:text-jr2-200 flex items-center gap-1.5"
          >
            <ChevronLeft size={14} /> {t('actions.back')}
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={() => setShowEditModal(true)}
              className="px-3 py-2 rounded-lg text-sm border border-base t-secondary hover:bg-hover hover:text-jr2-700 dark:hover:text-jr2-200 flex items-center gap-1.5"
            >
              <Pencil size={14} /> {t('actions.edit')}
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              onClick={() => setShowNewCase(true)}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
            >
              <Plus size={16} /> {t('opportunities:newButton')}
            </button>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-base flex items-center gap-1 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon
          const active = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={[
                'px-4 py-2.5 text-sm flex items-center gap-1.5 border-b-2 transition whitespace-nowrap',
                active
                  ? 'border-jr2-400 text-jr2-700 dark:text-jr2-200'
                  : 'border-transparent t-muted hover:t-primary hover:border-base',
              ].join(' ')}
            >
              <Icon size={14} /> {t(tab.labelKey)}
            </button>
          )
        })}
      </div>

      {/* Contenido por pestaña */}
      {activeTab === 'resumen' && (
        <ResumenTab
          client={client}
          opportunities={opportunities}
          invoices={invoices}
          milestones={milestones}
        />
      )}
      {activeTab === 'contactos' && (
        <div className="space-y-4">
          <ContactsPanel clientId={client.id} canWrite={canWrite} />
          {/* Las fechas señaladas van con los contactos y no en Comunicaciones:
              son de las PERSONAS del cliente, no de lo que se les ha escrito. */}
          <ClientDates clientId={client.id} canWrite={canWrite} />
        </div>
      )}
      {activeTab === 'oportunidades' && <OportunidadesTab opportunities={opportunities} />}
      {activeTab === 'proyectos' && <ProyectosTab opportunities={opportunities} />}
      {activeTab === 'facturacion' && (
        <FacturacionTab clientId={client.id} invoices={invoices} milestones={milestones} />
      )}
      {activeTab === 'comunicaciones' && (
        <ComunicacionesTab
          client={client}
          archivedEmails={archivedEmails}
          sentEmails={sentEmails}
          events={opportunityEvents}
        />
      )}

      {/* Modal de edición — reutilizamos el de Clients.tsx vía recarga, pero
          aquí abrimos un Modal embebido inline navegando al modal del listado.
          Simplificación: navegamos a /clients y abrimos el modal allí mediante
          query string sería ideal; por ahora un placeholder de re-load. */}
      {/* Mejora #4 — Nueva oportunidad desde la ficha del cliente, con el cliente
          prellenado. El propio modal navega al detalle de la oportunidad creada. */}
      {showNewCase && (
        <NewCaseModal
          defaultClientId={client.id}
          onClose={() => setShowNewCase(false)}
          onCreated={() => setShowNewCase(false)}
        />
      )}

      {showEditModal && (
        <ClientEditModalRedirect
          clientId={client.id}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            setShowEditModal(false)
            void reload()
          }}
        />
      )}
    </div>
  )
}

// ============== TABS ==============

function ResumenTab({
  client,
  opportunities,
  invoices,
  milestones,
}: {
  client: ClientDetail
  opportunities: OpportunityListItem[]
  invoices: InvoiceListItem[]
  milestones: MilestoneDto[]
}) {
  const { t } = useTranslation('clients')
  const ytd = useMemo(() => {
    const year = new Date().getFullYear()
    return invoices
      .filter(
        (inv) => inv.issueDate && new Date(inv.issueDate).getFullYear() === year && inv.status !== 'ANULADA'
      )
      .reduce((acc, inv) => acc + inv.totalCents, 0)
  }, [invoices])

  const activeOpps = useMemo(
    () => opportunities.filter((o) => o.stage !== 'GANADA' && o.stage !== 'PERDIDA').length,
    [opportunities]
  )
  const pendingMilestones = useMemo(
    () => milestones.filter((m) => m.status === 'PENDIENTE'),
    [milestones]
  )
  const nextDue = useMemo(() => {
    const upcoming = pendingMilestones
      .slice()
      .sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))[0]
    return upcoming?.dueDate ?? null
  }, [pendingMilestones])

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <KpiCard label={t('kpi.facturadoYtd')} value={formatCents(ytd)} icon={Receipt} accent="emerald" />
      <KpiCard
        label={t('kpi.oportunidadesActivas')}
        value={String(activeOpps)}
        icon={Target}
        accent="jr2"
      />
      <KpiCard
        label={t('kpi.hitosPendientes')}
        value={String(pendingMilestones.length)}
        icon={Wallet}
        accent="amber"
      />
      <KpiCard
        label={t('kpi.proximoVencimiento')}
        value={nextDue ? new Date(nextDue).toLocaleDateString(currentLocaleTag()) : '—'}
        icon={Calendar}
        accent="indigo"
      />
      {client.profitability.investedCostCents > 0 && (
        <KpiCard
          label={t('kpi.invertido')}
          value={formatCents(client.profitability.investedCostCents)}
          icon={Coins}
          accent="indigo"
          internal
        />
      )}
      <KpiCard
        label={t('kpi.cltvInterno')}
        value={formatCents(client.profitability.cltvCents)}
        icon={TrendingUp}
        accent="amber"
        internal
      />
      <KpiCard
        label={t('kpi.proyectosGanados')}
        value={String(client.profitability.wonProjectsCount)}
        icon={ClipboardList}
        accent="jr2"
      />
    </div>
  )
}

function OportunidadesTab({ opportunities }: { opportunities: OpportunityListItem[] }) {
  const { t } = useTranslation('clients')
  const { nameOf } = useServiceLines()
  if (opportunities.length === 0) {
    return <EmptyState icon={Target} text={t('empty.opportunities')} />
  }
  return (
    <div className="surface overflow-hidden">
      <ul className="divide-y divide-base/50">
        {opportunities.map((op) => (
          <li key={op.id}>
            <Link
              to={`/opportunities/${op.id}`}
              className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-hover/50"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-jr2-700 dark:text-jr2-300">{op.code}</span>
                  <span className="t-primary text-sm font-medium truncate">{op.title}</span>
                </div>
                <div className="t-faint text-xs mt-0.5">
                  {nameOf(op.serviceLine)} · {new Date(op.openedAt).toLocaleDateString(currentLocaleTag())}
                </div>
              </div>
              <StageBadge stage={op.stage} />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ProyectosTab({ opportunities }: { opportunities: OpportunityListItem[] }) {
  const { t } = useTranslation('clients')
  const { nameOf } = useServiceLines()
  // "Proyectos" = oportunidades GANADA. (executionPhase no viene en el
  // OpportunityListItem; se vería en el detalle.) Filtramos por stage.
  const projects = opportunities.filter(
    (o) => o.stage === 'GANADA' || o.stage === 'EN_INVERSION'
  )
  if (projects.length === 0) {
    return (
      <EmptyState
        icon={ClipboardList}
        text={t('empty.projects')}
      />
    )
  }
  return (
    <div className="space-y-3">
      <p className="t-muted text-sm">
        {t('projects.intro', { count: projects.length })}
      </p>
      <div className="surface overflow-hidden">
        <ul className="divide-y divide-base/50">
          {projects.map((op) => (
            <li key={op.id}>
              <Link
                to={`/projects/${op.id}`}
                className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-hover/50"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-jr2-700 dark:text-jr2-300">{op.code}</span>
                    <span className="t-primary text-sm font-medium truncate">{op.title}</span>
                  </div>
                  <div className="t-faint text-xs mt-0.5">
                    {nameOf(op.serviceLine)} · {t('projects.closed')}{' '}
                    {op.closedAt ? new Date(op.closedAt).toLocaleDateString(currentLocaleTag()) : '—'}
                  </div>
                </div>
                <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40">
                  {t('projects.inExecution')}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function FacturacionTab({
  clientId,
  invoices,
  milestones,
}: {
  clientId: string
  invoices: InvoiceListItem[]
  milestones: MilestoneDto[]
}) {
  const { t } = useTranslation('clients')
  const totals = useMemo(() => {
    const facturado = invoices
      .filter((i) => i.status !== 'ANULADA' && i.status !== 'BORRADOR')
      .reduce((acc, i) => acc + i.totalCents, 0)
    const cobrado = invoices.reduce((acc, i) => acc + i.paidCents, 0)
    const pendiente = Math.max(0, facturado - cobrado)
    return { facturado, cobrado, pendiente }
  }, [invoices])

  const pendingMilestones = milestones.filter((m) => m.status === 'PENDIENTE')

  return (
    <div className="space-y-5">
      <RenewalAlert clientId={clientId} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label={t('billing.facturado')} value={formatCents(totals.facturado)} icon={Receipt} accent="jr2" />
        <KpiCard label={t('billing.cobrado')} value={formatCents(totals.cobrado)} icon={Wallet} accent="emerald" />
        <KpiCard label={t('billing.pendiente')} value={formatCents(totals.pendiente)} icon={Wallet} accent="amber" />
      </div>

      <section className="surface overflow-hidden">
        <header className="px-4 py-3 border-b border-base flex items-center justify-between">
          <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
            <Receipt size={14} className="text-jr2-700 dark:text-jr2-300" /> {t('billing.facturasTitle', { count: invoices.length })}
          </h3>
        </header>
        {invoices.length === 0 ? (
          <p className="px-4 py-6 t-muted text-sm text-center">{t('billing.noInvoices')}</p>
        ) : (
          <ul className="divide-y divide-base/50">
            {invoices.map((inv) => (
              <li key={inv.id}>
                <Link
                  to={`/billing/${inv.id}`}
                  className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-hover/50"
                >
                  <div className="min-w-0">
                    <div className="t-primary text-sm font-medium">
                      {inv.seriesPrefix}-{inv.number ?? t('billing.draftNumber')}{' '}
                      {inv.year ? <span className="t-faint">/{inv.year}</span> : null}
                    </div>
                    <div className="t-faint text-xs mt-0.5">
                      {inv.issueDate
                        ? new Date(inv.issueDate).toLocaleDateString(currentLocaleTag())
                        : t('billing.notIssued')}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="t-primary text-sm font-mono">{formatCents(inv.totalCents)}</div>
                    <div className="t-faint text-[10px] uppercase tracking-wider">{inv.status}</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="surface overflow-hidden">
        <header className="px-4 py-3 border-b border-base flex items-center justify-between">
          <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
            <Calendar size={14} className="text-jr2-700 dark:text-jr2-300" /> {t('billing.milestonesTitle', { count: pendingMilestones.length })}
          </h3>
        </header>
        {pendingMilestones.length === 0 ? (
          <p className="px-4 py-6 t-muted text-sm text-center">{t('billing.noMilestones')}</p>
        ) : (
          <ul className="divide-y divide-base/50">
            {pendingMilestones.map((m) => (
              <li key={m.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="t-primary text-sm">{m.title}</div>
                  <div className="t-faint text-xs">
                    {new Date(m.dueDate).toLocaleDateString(currentLocaleTag())}
                    {m.opportunityCode ? ` · ${m.opportunityCode}` : ''}
                  </div>
                </div>
                <div className="t-primary text-sm font-mono">{formatCents(m.amountCents)}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function ComunicacionesTab({
  client,
  archivedEmails,
  sentEmails,
  events,
}: {
  client: ClientDetail
  archivedEmails: EmailListItem[]
  sentEmails: SentEmailItem[]
  events: OpportunityEventDto[]
}) {
  const { t } = useTranslation('clients')
  const [plantillasWa, setPlantillasWa] = useState<MarketingTemplateDto[]>([])

  useEffect(() => {
    void api
      .get<MarketingTemplateListResponse>('/marketing/templates')
      .then((r) => setPlantillasWa(r.items.filter((x) => x.channel === 'WHATSAPP')))
      .catch(() => setPlantillasWa([]))
  }, [])

  const movil = telefonoParaWhatsApp(client.mobile) ?? telefonoParaWhatsApp(client.phone)
  return (
    <div className="space-y-5">
      <section className="surface overflow-hidden">
        <header className="px-4 py-3 border-b border-base">
          <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
            <Send size={14} className="text-jr2-700 dark:text-jr2-300" /> {t('comms.sentTitle', { count: sentEmails.length })}
          </h3>
        </header>
        {sentEmails.length === 0 ? (
          <p className="px-4 py-6 t-muted text-sm text-center">
            {t('comms.noSent')}
          </p>
        ) : (
          <ul className="divide-y divide-base/50">
            {sentEmails.map((e) => (
              <li key={e.id} className="px-4 py-3">
                <div className="t-primary text-sm flex items-center gap-1.5">
                  <span className="truncate">{e.subject ?? t('comms.noSubject')}</span>
                  {e.campaignName && (
                    <span
                      className="shrink-0 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-200 border border-fuchsia-500/40"
                      title={e.campaignName}
                    >
                      <Megaphone size={9} /> {t('comms.campaignTag')}
                    </span>
                  )}
                </div>
                <div className="t-faint text-xs mt-0.5">
                  {t('comms.to')}: {e.toAddresses.join(', ')} · {new Date(e.sentAt).toLocaleString(currentLocaleTag())}
                  {e.status ? ` · ${e.status}` : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="surface overflow-hidden">
        <header className="px-4 py-3 border-b border-base">
          <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
            <Mail size={14} className="text-jr2-700 dark:text-jr2-300" /> {t('comms.archivedTitle', { count: archivedEmails.length })}
          </h3>
        </header>
        {archivedEmails.length === 0 ? (
          <p className="px-4 py-6 t-muted text-sm text-center">
            {t('comms.noArchived')}
          </p>
        ) : (
          <ul className="divide-y divide-base/50">
            {archivedEmails.map((e) => (
              <li key={e.id} className="px-4 py-3">
                <div className="t-primary text-sm truncate">{e.subject ?? t('comms.noSubject')}</div>
                <div className="t-faint text-xs mt-0.5">
                  {t('comms.from')}: {e.fromAddress} · {new Date(e.receivedAt).toLocaleString(currentLocaleTag())}
                </div>
                {e.snippet && <div className="t-muted text-xs mt-1 line-clamp-2">{e.snippet}</div>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="surface overflow-hidden">
        <header className="px-4 py-3 border-b border-base">
          <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
            <MessageSquare size={14} className="text-jr2-700 dark:text-jr2-300" /> {t('comms.notesTitle', { count: events.length })}
          </h3>
        </header>
        {events.length === 0 ? (
          <p className="px-4 py-6 t-muted text-sm text-center">
            {t('comms.noNotes')}
          </p>
        ) : (
          <ul className="divide-y divide-base/50">
            {events.slice(0, 50).map((ev) => (
              <li key={ev.id} className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-jr2-500/10 text-jr2-700 dark:text-jr2-300 border border-jr2-500/30">
                    {t('enums:OpportunityEventType.' + ev.type)}
                  </span>
                  <span className="t-primary text-sm">{ev.title}</span>
                </div>
                <div className="t-faint text-xs mt-0.5">
                  {new Date(ev.occurredAt).toLocaleString(currentLocaleTag())}
                  {ev.createdByName ? ` · ${ev.createdByName}` : ''}
                </div>
                {ev.description && (
                  <div className="t-muted text-xs mt-1 line-clamp-2">{ev.description}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* WhatsApp. ☠️ NO se manda nada desde aquí: se abre WhatsApp con el
          mensaje ya escrito y lo envía la persona. No hay proveedor, ni
          plantilla aprobada por Meta, ni base legal para un envío automático —
          y fingir un canal que no existe sería peor que no tenerlo. Esto es lo
          honesto y es lo que de verdad ahorra tiempo: no reescribir el mismo
          mensaje veinte veces. */}
      <section className="surface p-5">
        <div className="flex items-start gap-3">
          <MessageCircle size={18} className="text-emerald-600 dark:text-emerald-300 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h3 className="t-primary font-semibold text-sm">{t('comms.whatsappTitle')}</h3>
            {!movil ? (
              <p className="t-muted text-xs mt-1">{t('comms.whatsappNoPhone')}</p>
            ) : plantillasWa.length === 0 ? (
              <p className="t-muted text-xs mt-1">
                {t('comms.whatsappNoTemplates')}{' '}
                <Link to="/marketing/templates" className="text-jr2-700 dark:text-jr2-300 hover:underline">
                  {t('comms.whatsappGoTemplates')}
                </Link>
              </p>
            ) : (
              <>
                <p className="t-muted text-xs mt-1">{t('comms.whatsappHint')}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {plantillasWa.map((p) => (
                    <a
                      key={p.id}
                      href={`https://wa.me/${movil}?text=${encodeURIComponent(
                        p.bodyText
                          .replace(/\{\{\s*nombre\s*\}\}/gi, client.displayName)
                          .replace(/\{\{\s*email\s*\}\}/gi, client.email ?? ''),
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg text-xs bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/25 inline-flex items-center gap-1.5"
                    >
                      <MessageCircle size={12} /> {p.name}
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

// ============== Helpers ==============

function KpiCard({
  label,
  value,
  icon: Icon,
  accent,
  internal,
}: {
  label: string
  value: string
  icon: typeof FileText
  accent: 'jr2' | 'emerald' | 'amber' | 'indigo'
  internal?: boolean
}) {
  const { t } = useTranslation('clients')
  const accentCls = {
    jr2: 'bg-jr2-500/10 text-jr2-700 dark:text-jr2-300 border-jr2-500/30',
    emerald: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
    amber: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/40',
    indigo: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  }[accent]

  return (
    <div className="surface p-4 flex items-start gap-3">
      <span className={`w-10 h-10 rounded-lg grid place-items-center border ${accentCls}`}>
        <Icon size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="t-muted text-xs uppercase tracking-wider">{label}</span>
          {internal && (
            <span className="text-[9px] px-1 py-0.5 rounded border border-amber-500/40 text-amber-700 dark:text-amber-300/70">
              {t('kpi.internalBadge')}
            </span>
          )}
        </div>
        <div className="t-primary text-lg font-mono mt-1">{value}</div>
      </div>
    </div>
  )
}

function StageBadge({ stage }: { stage: PipelineStage }) {
  const { t } = useTranslation()
  const cls: Record<PipelineStage, string> = {
  CANCELADA: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/25',
  CONGELADA: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-200 border-cyan-500/40',
    LEAD: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30',
    PROSPECTO: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40',
    OPORTUNIDAD: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
    PROPUESTA_EN_PREPARACION: 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/40',
    PROPUESTA_ENVIADA: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30',
    EN_INVERSION: 'bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/40',
    GANADA: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
    PERDIDA: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
    EN_ESPERA: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  }
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded border whitespace-nowrap ${cls[stage]}`}
    >
      {t('enums:PipelineStage.' + stage)}
    </span>
  )
}

function EmptyState({ icon: Icon, text }: { icon: typeof FileText; text: string }) {
  return (
    <div className="surface p-10 text-center">
      <Icon size={24} className="mx-auto t-faint mb-2" />
      <p className="t-muted text-sm">{text}</p>
    </div>
  )
}

/**
 * Mini-redirect: para no duplicar el modal pesado de Clients.tsx (que mezcla
 * tipo, NIF, dirección, tarifas y rentabilidad), redirigimos al listado con un
 * query string que abrirá el modal allí. Es el enlace más simple sin extraer
 * el modal a un componente compartido.
 */
function ClientEditModalRedirect({
  clientId,
  onClose,
}: {
  clientId: string
  onClose: () => void
  onSaved: () => void
}) {
  const navigate = useNavigate()
  useEffect(() => {
    onClose()
    navigate(`/clients?edit=${clientId}`)
  }, [clientId, navigate, onClose])
  return null
}
